import { reservations, mediaUnits, reservationQueue, availabilityWindows } from '@ooh/contracts';
import type { ReservationStatus, ReservationSource } from '@ooh/contracts';
import { apiQueryKey } from '../../data/use-api-suspense-query';

// Query keys are `[method, path, request]` (see apiQueryKey), so web code that has to invalidate or
// share these caches builds its keys from the same endpoint definitions.

export const MEDIA_UNIT_AVAILABILITY_KEY_PREFIX = ['media-units', 'availability'] as const;

export const RESERVATION_LIST_KEY_PREFIX = apiQueryKey(reservations.list);

export interface ReservationListParams {
  status?: string[];
  holderUserId?: number[];
  offerId?: number[];
  mediaUnitId?: number[];
  source?: string[];
  clientId?: number[];
  salespersonId?: number[];
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  order?: string;
  hasQueue?: boolean;
}

export function reservationListRequest(params: ReservationListParams) {
  return {
    // The route's validateSearch (Zod) has already validated these string values
    // against the same enum schemas used by the API, so the casts are runtime-safe.
    status: params.status as ReservationStatus[] | undefined,
    holderUserId: params.holderUserId,
    offerId: params.offerId,
    mediaUnitId: params.mediaUnitId,
    source: params.source as ReservationSource[] | undefined,
    clientId: params.clientId,
    salespersonId: params.salespersonId,
    dateFrom: params.dateFrom ? new Date(params.dateFrom) : undefined,
    dateTo: params.dateTo ? new Date(params.dateTo) : undefined,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    sort: (params.sort ?? 'date_from') as 'date_from' | 'date_to' | 'created_at' | 'status',
    order: (params.order ?? 'asc') as 'asc' | 'desc',
    hasQueue: params.hasQueue,
  };
}

export function unitReservationsRequest(unitId: number) {
  return { id: unitId };
}

export function unitAuditEventsRequest(unitId: number) {
  return { id: unitId };
}

export function unitAvailabilityWindowsRequest(unitId: number) {
  return { id: unitId, limit: 100, offset: 0 };
}

/** Fetches a broad date range; the component correlates entries to reservation rows by window overlap. */
export function queueByUnitRequest(mediaUnitId: number) {
  return { mediaUnitId, dateFrom: '2000-01-01', dateTo: '2099-12-31' };
}

export function queueByReservationsRequest(reservationIds: number[]) {
  return { reservationId: reservationIds };
}

export const reservationQueryKeys = {
  lists: () => RESERVATION_LIST_KEY_PREFIX,
  list: (params: ReservationListParams) =>
    apiQueryKey(reservations.list, reservationListRequest(params)),
  byUnit: (unitId: number) =>
    apiQueryKey(mediaUnits.listReservations, unitReservationsRequest(unitId)),
  auditByUnit: (unitId: number) =>
    apiQueryKey(mediaUnits.listAuditEvents, unitAuditEventsRequest(unitId)),
  windowsByUnit: (unitId: number) =>
    apiQueryKey(availabilityWindows.list, unitAvailabilityWindowsRequest(unitId)),
  queueByUnit: (mediaUnitId: number) =>
    apiQueryKey(reservationQueue.listPosition, queueByUnitRequest(mediaUnitId)),
  queueByReservationsAll: () => apiQueryKey(reservationQueue.listByReservations),
} as const;
